import { v7 } from "uuid";
import { WithOwner } from "../IDBSchema";
import { EntityDataApi, IdbRowPayload } from "../IndexedDBApi";
import { ConstructionPlanAreaBase } from "../../../schema/generated/ConstructionPlanArea";
import { getFromIdPayload, setDeletePayload } from "../../../schema/general";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { idbEntityDefs, IDBEntityDef } from "../generated/entityDefs";
import Time from "../../../util/Time";
import { PatchConstructionPlanAreaWithChildrenPayload, PutConstructionPlanAreaWithChildrenPayload } from "../../../schema/console/construction_plan_area";

// 子(場所区分 / 計画材料)の1行分のペイロード型。put/patchとも construction_plan_area_id を持たない
// 同一の nested スキーマ(schema/console/construction_plan_area.ts)を使うため、put の型から流用する。
type LocationChildPayload = PutConstructionPlanAreaWithChildrenPayload["locations"][number];
type PlannedMaterialChildPayload = PutConstructionPlanAreaWithChildrenPayload["planned_materials"][number];

// 子(場所区分/計画材料)エンティティが共通して持つ列。カスケード対象の判定・行差替に必要な最小集合。
type ChildRow = { id: string; construction_plan_area_id: string; deleted_at: number | null };

export class ConstructionPlanAreaDataApi extends EntityDataApi<ConstructionPlanAreaBase> {
  public readonly def = idbEntityDefs.ConstructionPlanArea;

  public async getAll(ownerId: string): Promise<ConstructionPlanAreaBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<ConstructionPlanAreaBase | null> {
    return await this.findById(payload);
  }

  public async getByConstructionPlanId(payload: WithOwner<getFromIdPayload>): Promise<ConstructionPlanAreaBase[]> {
    return await this.readByIndex("construction_plan_id", payload.ownerId, payload.id);
  }

  // 子込み(locations / planned_materials)のオフライン新規作成。
  // サーバの /put-construction-plan-area は親をUUID採番して作成し、子は親idを付与して一括作成する。
  // 親1行+子N行を3ストア(ConstructionPlanAreas / ConstructionPlanAreaLocations /
  // ConstructionPlanAreaPlannedMaterials)へ1トランザクションで書き込み、途中失敗による
  // 「親だけ作成された/子だけ作成された」中途状態を防ぐ。行の組み立てはtx開始前に完了させ、
  // tx内はputのみを行う。
  // ConstructionPlanArea は hasPreparer:false だが、created_by_user_id 列(nullable)はサーバ側で
  // locals.user.id を格納する列であるため、ここでも ownerId を明示的に埋める。未設定のままだと
  // caches スキーマの必須キー欠落で merge push が 400 になり、オンライン復帰が失敗し続ける。
  public async put(data: WithOwner<PutConstructionPlanAreaWithChildrenPayload>): Promise<PutStatusResponse> {
    const { locations, planned_materials, ownerId, ...areaData } = data;
    const now = Time.Now().GetTime();
    const areaBaseColumns = this.addCreateBaseColumns(ownerId);
    const areaRow = { ...this.def.putDefaults, ...areaData, ...areaBaseColumns, created_by_user_id: ownerId };
    const areaId = areaBaseColumns.id;

    const locationRows = locations.map((location) => this.buildChildRow(this.locationApi().def, location, areaId, ownerId, now));
    const materialRows = planned_materials.map((material) => this.buildChildRow(this.materialApi().def, material, areaId, ownerId, now));

    await this.IDB().putRowsAtomic([
      { store: this.def.store, rows: [areaRow] },
      { store: this.locationApi().def.store, rows: locationRows },
      { store: this.materialApi().def.store, rows: materialRows },
    ]);

    return { status: "ok", id: areaId };
  }

  // 子込みの更新。locations / planned_materials が undefined でない場合のみ、
  // サーバと同じ delete-insert 意味論を IDB 上で再現する: 対象エリアの既存alive子行を
  // 論理削除(トンボストーン化)してから、送られた新リストを作成し直す。
  // undefined の場合は子に触れない(スカラー項目のみの更新)。
  // 親の更新値・子のトンボストーン・新規の子行はすべてtx開始前に組み立て、
  // 3ストアへの書込みを1トランザクションで原子的にコミットする(修正2)。
  public async patch(data: WithOwner<PatchConstructionPlanAreaWithChildrenPayload>): Promise<StatusResponse> {
    const { locations, planned_materials, ownerId, ...areaData } = data;
    const now = Time.Now().GetTime();

    const originalArea = await this.findById({ id: data.id, ownerId });
    if (!originalArea) {
      throw new Error(`${this.def.store}に更新対象のデータがありません。`);
    }
    const areaRow = { ...originalArea, ...areaData, ownerId, updatedAt: now };

    const perStore: { store: string; rows: IdbRowPayload[] }[] = [{ store: this.def.store, rows: [areaRow] }];
    if (locations !== undefined) {
      const locationRows = await this.buildReplaceRows(this.locationApi(), data.id, ownerId, locations, now);
      perStore.push({ store: this.locationApi().def.store, rows: locationRows });
    }
    if (planned_materials !== undefined) {
      const materialRows = await this.buildReplaceRows(this.materialApi(), data.id, ownerId, planned_materials, now);
      perStore.push({ store: this.materialApi().def.store, rows: materialRows });
    }

    await this.IDB().putRowsAtomic(perStore);
    return { status: "ok" };
  }

  // エリアの論理削除/復元。子2テーブルへのカスケードはコホート方式(サーバの
  // /set-delete-construction-plan-area と同じ): 削除時は alive の子のみを対象にし、
  // 復元時は「親と同時に削除された子(deleted_at が親の削除時刻と同一秒)」のみを戻す。
  // 全子行を無条件に上書きすると、子込み保存(delete-insert)の履歴トンボストーンまで復活してしまう。
  // 親1行+子カスケード行を1トランザクションで原子的に書き込む(修正2)。
  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    const parent = await this.findById({ id: data.id, ownerId: data.ownerId }, false);
    if (!parent) {
      throw new Error(`${this.def.store}に更新対象のデータがありません。`);
    }
    const previousDeletedAt = parent.deleted_at;

    if (data.deleted && previousDeletedAt != null) {
      // 既に削除済みの行への再削除は no-op。親の削除時刻を更新するとコホート(親子で共有する
      // 削除時刻)が崩れ、子の復元判定(同一ms/同一秒一致)が正しく機能しなくなるため何もしない。
      return { status: "ok" };
    }
    if (!data.deleted && previousDeletedAt != null) {
      await this.assertRestorable(data.id, data.ownerId);
    }

    // 親子で同一の削除時刻を共有する(コホート判定と、サーバの merge カスケードとの整合の要)。
    const now = Time.Now().GetTime();
    const deletedAt = data.deleted ? now : null;

    const areaRow = { ...parent, deleted_at: deletedAt, updatedAt: now };
    const locationRows = await this.buildCascadeRows(this.locationApi(), data.id, data.ownerId, data.deleted, previousDeletedAt, deletedAt, now);
    const materialRows = await this.buildCascadeRows(this.materialApi(), data.id, data.ownerId, data.deleted, previousDeletedAt, deletedAt, now);

    await this.IDB().putRowsAtomic([
      { store: this.def.store, rows: [areaRow] },
      { store: this.locationApi().def.store, rows: locationRows },
      { store: this.materialApi().def.store, rows: materialRows },
    ]);
    return { status: "ok" };
  }

  private locationApi() {
    return this.IDB().generated.constructionPlanAreaLocation;
  }

  private materialApi() {
    return this.IDB().generated.constructionPlanAreaPlannedMaterial;
  }

  // 子1行分の新規行を組み立てる(createRowと同等の合成: putDefaults → 入力データ → 共通カラムの順)。
  // 場所区分/計画材料は hasPreparer:false のため preparer_user_id 系の付与は不要。
  private buildChildRow(
    childDef: IDBEntityDef,
    payload: LocationChildPayload | PlannedMaterialChildPayload,
    areaId: string,
    ownerId: string,
    now: number,
  ): IdbRowPayload {
    const id = v7();
    return {
      ...childDef.putDefaults,
      ...payload,
      construction_plan_area_id: areaId,
      ownerId,
      id,
      createdAt: now,
      updatedAt: now,
      cachedAt: now,
    };
  }

  // 対象エリアの既存alive子行をトンボストーン化する行と、新リストから組み立てた新規行を合わせて返す。
  // 削除行・新規行のどちらもここで組み立て、呼出側でまとめて1tx put する。
  private async buildReplaceRows<TChild extends ChildRow>(
    childApi: EntityDataApi<TChild>,
    areaId: string,
    ownerId: string,
    payloadList: (LocationChildPayload | PlannedMaterialChildPayload)[],
    now: number,
  ): Promise<IdbRowPayload[]> {
    const existingRows = await childApi.getAllByOwner(ownerId, true);
    const トゥームストーンRows = existingRows
      .filter((row) => row.construction_plan_area_id === areaId)
      .map((row) => ({ ...row, deleted_at: now, updatedAt: now }));
    const newRows = payloadList.map((payload) => this.buildChildRow(childApi.def, payload, areaId, ownerId, now));
    return [...トゥームストーンRows, ...newRows];
  }

  // 削除/復元カスケード対象の子行を、deleted_at を書き換えた行として返す。
  // 削除時は alive の子のみを対象にする。
  // 復元時は「親と同時に削除された子」のみを戻す(全子行を無条件に上書きすると、子込み保存
  // (delete-insert)の履歴トンボストーンまで復活してしまう)。まず deleted_at の ms 完全一致を試し、
  // 1件も無い場合のみ秒 floor 一致にフォールバックする(サーバから再キャッシュされた親の deleted_at は
  // datetime 列で秒精度に丸められるため)。ms 一致を優先することで、同一秒内に複数コホートの削除が
  // 起きていた場合に別コホートのトンボストーンまで誤って復活させるのを防ぐ。
  private async buildCascadeRows<TChild extends ChildRow>(
    childApi: EntityDataApi<TChild>,
    areaId: string,
    ownerId: string,
    isDeleting: boolean,
    previousDeletedAt: number | null,
    deletedAt: number | null,
    now: number,
  ): Promise<IdbRowPayload[]> {
    const rows = await childApi.getAllByOwner(ownerId, false);
    const areaRows = rows.filter((row) => row.construction_plan_area_id === areaId);

    if (isDeleting) {
      const targetRows = areaRows.filter((row) => row.deleted_at == null);
      return targetRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
    }

    if (previousDeletedAt == null) {
      return [];
    }

    const exactMatchRows = areaRows.filter((row) => row.deleted_at === previousDeletedAt);
    if (exactMatchRows.length > 0) {
      return exactMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
    }

    const previousDeletedAtSecond = Math.floor(previousDeletedAt / 1000);
    const floorMatchRows = areaRows.filter((row) => row.deleted_at != null && Math.floor(row.deleted_at / 1000) === previousDeletedAtSecond);
    return floorMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
  }
}
